import React, { useState } from "react";
import styled from "@emotion/styled";
import { keyframes } from "@emotion/react";
import api from "../../api";
import { useNavigate } from "react-router-dom";
import { showError, showSuccess } from "../../utils/sweetAlertConfig";

// Animation keyframes
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const gradientShift = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

// Styled Components
const LoginContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(-45deg, #29204C, #1a1530, #29204C, #342854);
  background-size: 400% 400%;
  animation: ${gradientShift} 15s ease infinite;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 20% 80%, rgba(255,255,255,0.05) 0%, transparent 50%),
                radial-gradient(circle at 80% 20%, rgba(255,255,255,0.08) 0%, transparent 50%);
  }
`;

const LoginForm = styled.form`
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  box-shadow: 
    0 25px 50px rgba(41, 32, 76, 0.15),
    0 0 0 1px rgba(255, 255, 255, 0.1);
  padding: 48px 40px;
  min-width: 420px;
  max-width: 480px;
  width: 100%;
  margin: 20px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-items: center;
  animation: ${fadeInUp} 0.8s cubic-bezier(0.22, 1, 0.36, 1);
  position: relative;
  z-index: 1;

  @media (max-width: 500px) {
    min-width: auto;
    padding: 36px 28px;
    margin: 16px;
  }
`;

const Title = styled.h2`
  color: #29204C;
  margin-bottom: 8px;
  font-weight: 700;
  font-size: 28px;
  text-align: center;
  letter-spacing: -0.5px;

  @media (max-width: 500px) {
    font-size: 24px;
  }
`;

const Subtitle = styled.p`
  color: #666;
  font-size: 16px;
  margin-bottom: 16px;
  text-align: center;
  font-weight: 400;
`;

const InputGroup = styled.div`
  width: 100%;
  position: relative;
`;

const StyledInput = styled.input`
  width: 100%;
  padding: 16px 20px;
  border-radius: 12px;
  border: 2px solid ${props => 
    props.hasError ? '#EF4444' : 
    props.isFocused ? '#68B977' : 
    props.hasValue ? '#68B977' : '#E5E7EB'
  };
  font-size: 16px;
  background: ${props => props.hasError ? '#FEF2F2' : '#FAFAFA'};
  color: #1F2937;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  outline: none;
  font-weight: 500;

  &::placeholder {
    color: #9CA3AF;
    font-weight: 400;
  }

  &:focus {
    background: #fff;
    box-shadow: 0 0 0 4px ${props => 
      props.hasError ? 'rgba(239, 68, 68, 0.1)' : 'rgba(104, 185, 119, 0.1)'
    };
    transform: translateY(-1px);
  }

  &:hover {
    border-color: ${props => 
      props.hasError ? '#EF4444' : 
      props.hasValue ? '#68B977' : '#CBD5E1'
    };
  }
`;

const ErrorMessage = styled.div`
  color: #EF4444;
  font-size: 14px;
  margin-top: 6px;
  font-weight: 500;
  opacity: ${props => props.show ? 1 : 0};
  transform: translateY(${props => props.show ? '0' : '-10px'});
  transition: all 0.3s ease;
`;

const SubmitButton = styled.button`
  width: 100%;
  background: linear-gradient(135deg, #29204C 0%, #1a1530 100%);
  color: #fff;
  border: none;
  border-radius: 12px;
  padding: 16px 24px;
  font-weight: 600;
  font-size: 16px;
  margin-top: 8px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
    transition: left 0.5s;
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(41, 32, 76, 0.3);
    
    &::before {
      left: 100%;
    }
  }

  &:active {
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
    
    &:hover {
      transform: none;
      box-shadow: none;
    }
  }
`;

const Footer = styled.div`
  color: #888;
  font-size: 13px;
  margin-top: 16px;
  text-align: center;
  font-weight: 400;
`;

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [focusedField, setFocusedField] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  // Email validation
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Handle email change
  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    
    if (value && !validateEmail(value)) {
      setEmailError("Please enter a valid email address");
    } else {
      setEmailError("");
    }
  };

  // Handle password change
  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
    
    if (value && value.length < 3) {
      setPasswordError("Password is too short");
    } else {
      setPasswordError("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate fields
    let hasErrors = false;
    
    if (!email) {
      setEmailError("Email is required");
      hasErrors = true;
    } else if (!validateEmail(email)) {
      setEmailError("Please enter a valid email address");
      hasErrors = true;
    }
    
    if (!password) {
      setPasswordError("Password is required");
      hasErrors = true;
    } else if (password.length < 3) {
      setPasswordError("Password is too short");
      hasErrors = true;
    }
    
    if (hasErrors) {
      showError("Validation Error", "Please fix the errors above and try again.");
      return;
    }

    setIsLoading(true);
    
    try {
      const res = await api.post("/login", { email, password });
      localStorage.setItem("token", res.data.token);
      
      showSuccess("Welcome!", "Login successful. Redirecting to dashboard...");
      
      setTimeout(() => {
        navigate("/dashboard");
      }, 1500);
      
    } catch (err) {
      setIsLoading(false);
      showError(
        "Login Failed", 
        "Invalid email or password. Please check your credentials and try again."
      );
    }
  };

  return (
    <LoginContainer>
      <LoginForm onSubmit={handleSubmit}>
        <Title>Sign in to ZonNet Admin</Title>
        <Subtitle>Enter your credentials to access the admin panel</Subtitle>
        
        <InputGroup>
          <StyledInput
            type="email"
            placeholder="Email address"
            value={email}
            onChange={handleEmailChange}
            onFocus={() => setFocusedField("email")}
            onBlur={() => setFocusedField("")}
            isFocused={focusedField === "email"}
            hasValue={email.length > 0}
            hasError={emailError.length > 0}
            required
          />
          <ErrorMessage show={emailError.length > 0}>
            {emailError}
          </ErrorMessage>
        </InputGroup>

        <InputGroup>
          <StyledInput
            type="password"
            placeholder="Password"
            value={password}
            onChange={handlePasswordChange}
            onFocus={() => setFocusedField("password")}
            onBlur={() => setFocusedField("")}
            isFocused={focusedField === "password"}
            hasValue={password.length > 0}
            hasError={passwordError.length > 0}
            required
          />
          <ErrorMessage show={passwordError.length > 0}>
            {passwordError}
          </ErrorMessage>
        </InputGroup>

        <SubmitButton type="submit" disabled={isLoading}>
          {isLoading ? "Signing in..." : "Sign In"}
        </SubmitButton>

        <Footer>
          &copy; {new Date().getFullYear()} ZonNet. All rights reserved.
        </Footer>
      </LoginForm>
    </LoginContainer>
  );
} 